import React from "react";
//#region types
import { Customer } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { InvoiceListViewProps } from "models/propTypes/invoicePropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { InvoiceFormTypes } from "models/formTypes";
import { IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { CurrencyValueFormatter, Icon, TableAsync, useFilterContext } from "components/common";
import InvoicePaymentStatus from "./paymentStatus";
import InvoiceStatusTags from "./statusTags";
import { Profiles } from "components/common";
import { Grid } from "@mui/material";
import { SimpleTooltip } from "components/common";
import { useInvoiceService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { CurrencyHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const InvoiceListView: React.ForwardRefRenderFunction<TableHandles, InvoiceListViewProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const { filter } = useFilterContext<InvoiceFormTypes.Filter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => invoiceService.reportFilter(baseFilter, props.filter ?? filter),
    [filter]
  );

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/Invoice/${id}`);

  return (
    <TableAsync<ApiModel.InvoiceListItem>
      tableId={props.tableKey ?? "invoices"}
      tableRef={ref}
      properties={{
        ownerName: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Owner),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceDateHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => (
            <Grid container direction="row" spacing={1} alignItems="left" wrap="nowrap">
              <Grid item>
                <Profiles.UserProfileLink id={values.ownerId} name={values.ownerName} hideUserName={true} />
              </Grid>
              {values.fromRecurring && (
                <SimpleTooltip
                  text={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Invoice.RecurringInvoiceIconToolTip
                  )}
                >
                  <Grid item>
                    <Icon
                      icon={IconDefinitionConstants.WRecurring}
                      displayType={StyleEnums.DisplayType.None}
                      className={cssExports.listIcon}
                    />
                  </Grid>
                </SimpleTooltip>
              )}
              {values.fromQuotation && (
                <SimpleTooltip
                  text={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Quotation)}
                >
                  <Grid item>
                    <Icon
                      icon={IconDefinitionConstants.WCreate}
                      displayType={StyleEnums.DisplayType.None}
                      className={cssExports.listIcon}
                    />
                  </Grid>
                </SimpleTooltip>
              )}
            </Grid>
          ),
          valueToPrint: (values: ApiModel.InvoiceListItem) => values.ownerName ?? "",
          alignment: "left",
          width: 40,
          noWrap: true,
        },
        postingDate: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceDate),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceDateHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => values.postingDate && new Date(values.postingDate),
          noWrap: true,
          width: 120,
        },
        dueDate: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Due_Date),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.DueDateHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => values.dueDate && new Date(values.dueDate),
          noWrap: true,
          width: 120,
        },
        customerName: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Customer),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CustomerHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => new Customer(values.customerId, values.customerName),
          noWrap: true,
        },
        serialNumber: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Number),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceNumberHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => values.serialNumber,
          noWrap: true,
        },
        totalAmount: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.TotalAmount),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoicedAmountHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => new Amount(values.currency, values.totalAmount),
          alignment: "right",
          summaryValueFormatter: (values: ApiModel.InvoiceListItem) => (
            <CurrencyValueFormatter sumValue={values.totalAmountSummarySEK} cellValue={values.totalAmountSummary} />
          ),
          summaryToPrint: (values: ApiModel.InvoiceListItem) =>
            CurrencyHelper.getSummaryToPrintWithSum(values.totalAmountSummarySEK, values.totalAmountSummary),
          noWrap: true,
        },
        totalAmountExcludingTax: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AmountExVat2),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AmountExVatHelpText),
          alignment: "right",
          formatter: (values: ApiModel.InvoiceListItem) => new Amount(values.currency, values.totalAmountExcludingTax),
          summaryValueFormatter: (values: ApiModel.InvoiceListItem) => (
            <CurrencyValueFormatter
              sumValue={values.amountExcludingTaxSummarySEK}
              cellValue={values.amountExcludingTaxSummary}
            />
          ),
          summaryToPrint: (values: ApiModel.InvoiceListItem) =>
            CurrencyHelper.getSummaryToPrintWithSum(
              values.amountExcludingTaxSummarySEK,
              values.amountExcludingTaxSummary
            ),
          noWrap: true,
          classification: "amount",
        },
        leftToPay: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid2),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.UnpaidAmoutHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => new Amount(values.currency, values.leftToPay),
          className: cssExports.textBlack,
          alignment: "right",
          summaryValueFormatter: (values: ApiModel.InvoiceListItem) => (
            <CurrencyValueFormatter sumValue={values.amountLeftSummarySEK} cellValue={values.leftToPaySummary} />
          ),
          summaryToPrint: (values: ApiModel.InvoiceListItem) =>
            CurrencyHelper.getSummaryToPrintWithSum(values.amountLeftSummarySEK, values.leftToPaySummary),
          noWrap: true,
          classification: "amount",
        },
        status: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Status),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceStatusHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => (
            <InvoiceStatusTags
              status={values.status}
              creditStatus={values.creditStatus}
              reminderInvoicesCount={values.reminderInvoicesCount}
              rot={values.rot}
              rut={values.rut}
              houseWorkApplicationIsFiled={values.houseWorkApplicationIsFiled}
              hasUnbookedHouseWorkCases={values.hasUnbookedHouseWorkCases}
            />
          ),
          noWrap: true,
          valueToPrint: (values: ApiModel.InvoiceListItem) => invoiceService.getStateText(values.status),
        },
        paymentState: {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid2),
          helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PaymentStatusHelpText),
          formatter: (values: ApiModel.InvoiceListItem) => <InvoicePaymentStatus status={values.paymentState} />,
          alignment: "center",
          width: 50,
          valueToPrint: (values: ApiModel.InvoiceListItem) => invoiceService.getPaymentStateText(values.paymentState),
        },
      }}
      orderByProperty={"postingDate"}
      orderByDescending
      filter={filterFunc}
      pagination="advanced"
      includeSummary={props.includeSummary}
      maxRowCount={10}
      itemUrl={getItemUrl}
      reloadKey={props.reloadKey}
      exportFileName={
        props.exportFileName
          ? props.exportFileName
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CustomerInvoices)
      }
    />
  );
};
export default React.forwardRef(InvoiceListView);
